import React, { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { FiLock, FiUnlock } from 'react-icons/fi';
import CenterModalWindow from '../../layout/ReusableComponent/CenterModalWindow/CenterModalWindow';
import { storageBoxApi } from '../api/storageBoxApi';

function modalIcon(mode) {
  if (mode === 'unlock' || mode === 'shared') return FiUnlock;
  return FiLock;
}

export default function StorageBoxLockPinModal({
  mode,
  shareId,
  onClose,
  onSuccess,
  onSwitchMode,
  onNotify,
}) {
  const [currentPin, setCurrentPin] = useState('');
  const [pin, setPin] = useState('');
  const [confirmPin, setConfirmPin] = useState('');
  const [tempPin, setTempPin] = useState('');
  const [busy, setBusy] = useState(false);
  const [resetRequested, setResetRequested] = useState(false);

  const currentPinRef = useRef(null);
  const tempPinRef = useRef(null);
  const pinRef = useRef(null);

  const focusPrimaryField = useCallback(() => {
    const el =
      mode === 'change'
        ? currentPinRef.current
        : mode === 'reset'
          ? tempPinRef.current
          : pinRef.current;
    if (!(el instanceof HTMLInputElement)) return;
    el.focus({ preventScroll: true });
  }, [mode]);

  useLayoutEffect(() => {
    if (!mode) return undefined;
    let cancelled = false;
    const run = () => {
      if (!cancelled) focusPrimaryField();
    };
    const raf1 = requestAnimationFrame(() => {
      requestAnimationFrame(run);
    });
    const retryDelays = [0, 80, 200, 400];
    const timers = retryDelays.map((ms) => window.setTimeout(run, ms));
    return () => {
      cancelled = true;
      cancelAnimationFrame(raf1);
      timers.forEach((id) => window.clearTimeout(id));
    };
  }, [mode, focusPrimaryField]);

  const Icon = useMemo(() => modalIcon(mode), [mode]);

  const title =
    mode === 'setup'
      ? 'Create Lock folder PIN'
      : mode === 'change'
        ? 'Change Lock folder PIN'
        : mode === 'reset'
          ? 'Set new Lock folder PIN'
          : mode === 'shared'
            ? 'Enter temporary PIN'
            : 'Unlock Lock folder';

  const submitLabel =
    mode === 'setup'
      ? 'Create PIN'
      : mode === 'change'
        ? 'Update PIN'
        : mode === 'reset'
          ? 'Save new PIN'
          : mode === 'shared'
            ? 'Unlock shared folder'
            : 'Unlock';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (mode === 'setup') {
        const res = await storageBoxApi.lockSetup(pin, confirmPin);
        await onSuccess?.(res);
        onNotify?.('Lock folder is ready');
        onClose?.();
      } else if (mode === 'change') {
        const res = await storageBoxApi.lockChangePin({
          currentPin,
          newPin: pin,
          confirmPin,
        });
        await onSuccess?.(res);
        onNotify?.('Lock folder PIN updated');
        onClose?.();
      } else if (mode === 'reset') {
        const res = await storageBoxApi.lockCompletePinReset({
          tempPin,
          newPin: pin,
          confirmPin,
        });
        await onSuccess?.(res);
        onNotify?.('Lock folder PIN updated');
        onClose?.();
      } else if (mode === 'shared') {
        const res = await storageBoxApi.lockUnlockShared(shareId, pin);
        await onSuccess?.(res);
        onClose?.();
      } else {
        const res = await storageBoxApi.lockUnlock(pin);
        await onSuccess?.(res);
        onClose?.();
      }
    } catch (err) {
      const message = err.message || 'Failed';
      onNotify?.(message);
      const pinRejected =
        err.code === 'LOCK_PIN_INVALID' ||
        /incorrect pin|incorrect temporary pin|invalid temporary pin/i.test(message);
      if (pinRejected && (mode === 'unlock' || mode === 'shared' || mode === 'reset' || mode === 'change')) {
        onClose?.();
      }
    } finally {
      setBusy(false);
    }
  };

  const handleRequestReset = async () => {
    setBusy(true);
    try {
      await storageBoxApi.lockRequestPinReset();
      setResetRequested(true);
      onNotify?.('Check your registered email for a temporary PIN (valid 24 hours)');
    } catch (err) {
      onNotify?.(err.message || 'Could not send reset email');
    } finally {
      setBusy(false);
    }
  };

  return (
    <CenterModalWindow
      isOpen={Boolean(mode)}
      onClose={onClose}
      width="min(400px, calc(100vw - 32px))"
      height="auto"
      contentStyle={{ height: 'auto' }}
      contentClassName="sb-lock-pin-modal"
    >
      <form className="sb-name-modal__body sb-lock-pin-modal__body" onSubmit={handleSubmit}>
        <header className="sb-lock-pin-modal__head">
          <span className="sb-lock-pin-modal__icon" aria-hidden>
            <Icon size={22} strokeWidth={1.75} />
          </span>
          <h2 className="sb-name-modal__title sb-lock-pin-modal__title">{title}</h2>
        </header>

        {mode === 'setup' ? (
          <p className="sb-lock-pin-modal__hint">
            Choose a PIN or password. Lock folder contents stay encrypted and hidden from the main Storage Box
            view.
          </p>
        ) : null}

        {mode === 'change' ? (
          <label className="sb-name-modal__field">
            <span>Current PIN</span>
            <input
              ref={currentPinRef}
              type="password"
              autoComplete="current-password"
              autoFocus
              value={currentPin}
              onChange={(e) => setCurrentPin(e.target.value)}
              required
              minLength={4}
              disabled={busy}
            />
          </label>
        ) : null}

        {mode === 'reset' ? (
          <label className="sb-name-modal__field">
            <span>Temporary PIN from email</span>
            <input
              ref={tempPinRef}
              type="password"
              inputMode="numeric"
              autoComplete="one-time-code"
              autoFocus
              value={tempPin}
              onChange={(e) => setTempPin(e.target.value)}
              required
              disabled={busy}
            />
          </label>
        ) : null}

        <label className="sb-name-modal__field">
          <span>{mode === 'setup' || mode === 'reset' || mode === 'change' ? 'New PIN' : 'PIN'}</span>
          <input
            ref={pinRef}
            type="password"
            autoComplete={mode === 'setup' || mode === 'reset' || mode === 'change' ? 'new-password' : 'current-password'}
            autoFocus={mode !== 'change' && mode !== 'reset'}
            value={pin}
            onChange={(e) => setPin(e.target.value)}
            required
            minLength={4}
            disabled={busy}
          />
        </label>

        {mode === 'setup' || mode === 'reset' || mode === 'change' ? (
          <label className="sb-name-modal__field">
            <span>Confirm PIN</span>
            <input
              type="password"
              autoComplete="new-password"
              value={confirmPin}
              onChange={(e) => setConfirmPin(e.target.value)}
              required
              minLength={4}
              disabled={busy}
            />
          </label>
        ) : null}

        {mode === 'unlock' ? (
          <p className="sb-lock-pin-modal__text-actions">
            {resetRequested ? (
              <span className="sb-lock-pin-modal__hint-inline">
                Email sent. When you have the temporary PIN, use{' '}
                <button
                  type="button"
                  className="sb-lock-pin-modal__text-link"
                  onClick={() => onSwitchMode?.('reset')}
                  disabled={busy}
                >
                  set new PIN
                </button>
                .
              </span>
            ) : (
              <>
                <button
                  type="button"
                  className="sb-lock-pin-modal__text-link"
                  onClick={handleRequestReset}
                  disabled={busy}
                >
                  Reset Lock folder PIN
                </button>
                <span className="sb-lock-pin-modal__text-sep"> · </span>
                <button
                  type="button"
                  className="sb-lock-pin-modal__text-link"
                  onClick={() => onSwitchMode?.('reset')}
                  disabled={busy}
                >
                  I have a temporary PIN
                </button>
              </>
            )}
          </p>
        ) : null}

        <div className="sb-name-modal__actions sb-lock-pin-modal__actions">
          <button type="button" className="sb-secondary-btn" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button type="submit" className="sb-primary-btn sb-name-modal__submit" disabled={busy}>
            {busy ? 'Please wait…' : submitLabel}
          </button>
        </div>
      </form>
    </CenterModalWindow>
  );
}
